// The state behind "enlarge this card". A card offers an expander; clicking it
// moves that card's live DOM into the overlay layer's host (`Teleport`), which
// is the whole mechanism: the component instance never unmounts, so its chart
// keeps its own resize observer and its clicks keep their closures. Nothing is
// copied, re-rendered from props or opened in a second window.
//
// The state lives here rather than in the layer because the card that asks to
// be enlarged is rendered elsewhere in the tree (inside the question slot), and
// a card has to be able to read its own open state without a provider.
import { ref } from "vue";

/** The id of the layer's persistent host element. Cards teleport to
 *  `#analysis-card-host`; the layer renders that element once and never
 *  removes it, so a teleport target always exists. */
export const ANALYSIS_CARD_HOST_ID = "analysis-card-host";

/** The card filling the overlay, or null. One at a time: the overlay covers the
 *  pane, so a second card cannot be asked for while one is open. */
export const expandedCardId = ref<string | null>(null);

/** Enlarge the card with this id. Its `Teleport` then resolves against the
 *  layer's host, and the layer's `v-show` reveals the backdrop and shell. */
export function expandCard(id: string): void {
	expandedCardId.value = id;
}

/** Close the overlay and return the card to the pane, in place. Called by the
 *  X, the backdrop, Escape, and by a card that unmounts while open. */
export function collapseCard(): void {
	expandedCardId.value = null;
}

/** True while the layer is mounted. A card whose overlay host is absent must not
 *  offer the control, or its content would vanish into a target that is not there
 *  (ReportPanel is also rendered by the waste panels — the feature detect keeps
 *  that safe if it is ever hosted elsewhere). */
export const cardHostMounted = ref(false);

/** Bumped every time the layer mounts. A card keys its `Teleport` on this, so a
 *  card that outlives a layer re-resolves the host when a new one arrives: a
 *  Teleport caches the target it resolved at mount, and a card still holding the
 *  removed layer's host would move its content into that detached element the
 *  next time it was expanded — the card would simply disappear. */
export const cardHostGeneration = ref(0);
